import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';

// Layouts
import AuthLayout from '@/components/layout/AuthLayout';
import DashboardLayout from '@/components/layout/DashboardLayout';

// Auth Pages
import Login from '@/pages/auth/Login';

// Admin Pages
import AdminDashboard from '@/pages/admin/Dashboard';
import AdminUsers from '@/pages/admin/Users';
import AdminUserDetail from '@/pages/admin/UserDetail';
import AdminLogs from '@/pages/admin/Logs';
import AdminPackages from '@/pages/admin/Packages';
import AdminWvds from '@/pages/admin/Wvds';
import AdminBackup from '@/pages/admin/Backup';
import AdminDiagnostics from '@/pages/admin/Diagnostics';

// User Pages
import UserDashboard from '@/pages/user/Dashboard';
import DaznAccount from '@/pages/user/DaznAccount';
import Proxies from '@/pages/user/Proxies';
import XaccelServers from '@/pages/user/XaccelServers';
import Sports from '@/pages/user/Sports';
import Competitions from '@/pages/user/Competitions';
import Teams from '@/pages/user/Teams';
import Schedule from '@/pages/user/Schedule';
import LinearChannels from '@/pages/user/LinearChannels';
import Logs from '@/pages/user/Logs';
import Resolutions from '@/pages/user/Resolutions';
import Settings from '@/pages/user/Settings';
import Epg from '@/pages/user/Epg';
import CdnManagement from '@/pages/user/CdnManagement';
import Account from '@/pages/user/Account';
import SetupWizard from '@/pages/user/SetupWizard';
import AccountExpiredOverlay from '@/components/AccountExpiredOverlay';

// Protected Route Component
function ProtectedRoute({
  children,
  requireAdmin = false,
  allowSetupIncomplete = false,
}: {
  children: React.ReactNode;
  requireAdmin?: boolean;
  allowSetupIncomplete?: boolean;
}) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect to setup wizard if setup not completed (non-admin only)
  if (!allowSetupIncomplete && user?.role !== 'admin' && !user?.setupCompleted) {
    return <Navigate to="/setup" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { isAuthenticated, user } = useAuthStore();

  return (
    <>
    <Routes>
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route
          path="/login"
          element={
            isAuthenticated
              ? <Navigate to={
                  user?.role === 'admin'
                    ? '/admin'
                    : user?.setupCompleted
                      ? '/dashboard'
                      : '/setup'
                } replace />
              : <Login />
          }
        />
      </Route>

      {/* Setup Wizard (standalone, no DashboardLayout) */}
      <Route
        path="/setup"
        element={
          <ProtectedRoute allowSetupIncomplete>
            {user?.setupCompleted ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <SetupWizard />
            )}
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:id" element={<AdminUserDetail />} />
        <Route path="logs" element={<AdminLogs />} />
        <Route path="diagnostics" element={<AdminDiagnostics />} />
        <Route path="packages" element={<AdminPackages />} />
        <Route path="wvds" element={<AdminWvds />} />
        <Route path="backup" element={<AdminBackup />} />
        <Route path="account" element={<Account />} />
      </Route>

      {/* User Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<UserDashboard />} />
        <Route path="dazn-account" element={<DaznAccount />} />
        <Route path="proxies" element={<Proxies />} />
        <Route path="streaming-servers" element={<XaccelServers />} />
        <Route path="xaccel-servers" element={<Navigate to="/streaming-servers" replace />} />
        <Route path="resolutions" element={<Resolutions />} />
        <Route path="sports" element={<Sports />} />
        <Route path="competitions" element={<Competitions />} />
        <Route path="teams" element={<Teams />} />
        <Route path="schedule" element={<Schedule />} />
        <Route path="epg" element={<Epg />} />
        <Route path="linear-channels" element={<LinearChannels />} />
        <Route path="cdn" element={<CdnManagement />} />
        <Route path="logs" element={<Logs />} />
        <Route path="account" element={<Account />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <AccountExpiredOverlay />
    </>
  );
}

export default App;
